import React, { useEffect, useState } from 'react';
import { Button, Card, Empty, message, Spin, Table } from 'antd';
import { Link } from 'react-router-dom';
import profileService from '../../../services/profileService';

const storageKey = 'pa_salary_statistic_years';
const statisticRecordStorageKey = 'pa_salary_statistic_records';

const text = {
  title: '\u5de5\u8d44\u7edf\u8ba1',
  createYear: '\u521b\u5efa\u65b0\u7684\u4e00\u5e74',
  month: '\u6708',
  created: '\u5e74\u4efd\u5df2\u521b\u5efa',
  currentYear: '\u5f53\u524d\u5e74\u4efd',
  delete: '\u5220\u9664',
  salary: '\u5de5\u8d44',
  teacher: '\u8001\u5e08',
  hourlyTotal: '\u8bfe\u65f6\u8d39\u603b\u8ba1',
  performanceCommission: '\u4e1a\u7ee9\u63d0\u6210',
  total: '\u603b\u8ba1',
  period: '\u65f6\u95f4',
  sentAt: '\u53d1\u9001\u65f6\u95f4',
  back: '\u8fd4\u56de\u5de5\u8d44\u7edf\u8ba1',
  backMonth: '\u8fd4\u56de\u6708\u4efd',
  loadFailed: '\u5de5\u8d44\u7edf\u8ba1\u8bfb\u53d6\u5931\u8d25',
  hourlyFee: '\u8bfe\u65f6\u8d39',
  commission: '\u4e1a\u7ee9',
  wage: '\u5de5\u8d44',
  project: '\u4e0a\u8bfe\u7c7b\u578b',
  amount: '\u5de5\u8d44',
  count: '\u6b21\u6570',
  settlement: '\u7ed3\u7b97',
  studentName: '\u540d\u5b57',
  courseTypeDisplayName: '\u8bfe\u7a0b\u7c7b\u522b\u540d\u79f0',
  actualReceipt: '\u5b9e\u9645\u6536\u6b3e',
  referralCommissionAmount: '\u63a8\u8350\u4eba\u63d0\u6210',
  performanceIncome: '\u4e1a\u7ee9\u6536\u5165',
};

const getStoredYears = () => {
  try {
    const years = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(years) ? years : [];
  } catch (err) {
    return [];
  }
};

const getStoredSalaryRecords = () => {
  try {
    const records = JSON.parse(localStorage.getItem(statisticRecordStorageKey) || '[]');
    return Array.isArray(records) ? records : [];
  } catch (err) {
    return [];
  }
};

const totalText = (value) => Number(value || 0).toFixed(1);

const getSettlementMonthKey = (settlement) => {
  const dateText = settlement.statisticMonth || settlement.from || settlement.createdAt || settlement.to || '';
  return String(dateText).slice(0, 7).replace('/', '-');
};

const settledHourlyColumns = [
  { title: text.project, dataIndex: 'name', key: 'name' },
  { title: text.amount, dataIndex: 'amount', key: 'amount' },
  { title: text.count, dataIndex: 'count', key: 'count' },
  { title: text.settlement, dataIndex: 'settlement', key: 'settlement' },
];

const settledCommissionColumns = [
  { title: text.studentName, dataIndex: 'studentName', key: 'studentName', width: '15%' },
  {
    title: text.courseTypeDisplayName,
    dataIndex: 'courseTypeDisplayName',
    key: 'courseTypeDisplayName',
    width: '15%',
  },
  { title: text.actualReceipt, dataIndex: 'total_aud', key: 'total_aud', width: '15%' },
  {
    title: text.performanceCommission,
    dataIndex: 'performanceCommissionAmount',
    key: 'performanceCommissionAmount',
    width: '15%',
  },
  {
    title: text.referralCommissionAmount,
    dataIndex: 'referralCommissionAmount',
    key: 'referralCommissionAmount',
    width: '15%',
  },
  {
    title: text.performanceIncome,
    dataIndex: 'performanceIncome',
    key: 'performanceIncome',
    width: '25%',
  },
];

const settledWageColumns = [
  { title: text.hourlyTotal, dataIndex: 'hourlyTotal', key: 'hourlyTotal' },
  {
    title: text.performanceCommission,
    dataIndex: 'performanceCommissionTotal',
    key: 'performanceCommissionTotal',
  },
  { title: text.total, dataIndex: 'total', key: 'total' },
];

const SalaryStatisticMonth = ({ year, month }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => {
    profileService
      .getTeachers(true)
      .then((res) => {
        const teachers = res.data || [];
        const nextRows = teachers.reduce((result, teacher) => {
          const settlements = teacher.salaryStatisticSettlements || teacher.salarySettlements || [];
          settlements
            .filter((settlement) => getSettlementMonthKey(settlement) === monthKey)
            .forEach((settlement, index) => {
              const wage = (settlement.wageRows || [])[0] || {};
              result.push({
                key: `${teacher._id}-${settlement.key || index}`,
                teacherId: teacher._id,
                teacher: teacher.cnname || teacher.name,
                statisticMonth: getSettlementMonthKey(settlement),
                hourlyTotal: wage.hourlyTotal || 0,
                performanceCommissionTotal: wage.performanceCommissionTotal || 0,
                total: wage.total || 0,
                period: `${settlement.from || '-'} - ${settlement.to || '-'}`,
                sentAt: settlement.createdAt ? String(settlement.createdAt).slice(0, 10) : '-',
              });
            });
          return result;
        }, []);
        const localRecords = getStoredSalaryRecords().filter(
          (settlement) => getSettlementMonthKey(settlement) === monthKey
        );
        localRecords.forEach((settlement) => {
          const exists = nextRows.some(
            (row) =>
              row.teacherId === settlement.teacherId &&
              row.statisticMonth === monthKey
          );
          if (!exists) {
            const wage = (settlement.wageRows || [])[0] || {};
            nextRows.push({
              key: `${settlement.teacherId}-${settlement.key || settlement.statisticMonth}`,
              teacherId: settlement.teacherId,
              teacher: settlement.teacherName,
              statisticMonth: settlement.statisticMonth,
              hourlyTotal: wage.hourlyTotal || 0,
              performanceCommissionTotal: wage.performanceCommissionTotal || 0,
              total: wage.total || 0,
              period: `${settlement.from || '-'} - ${settlement.to || '-'}`,
              sentAt: settlement.createdAt ? String(settlement.createdAt).slice(0, 10) : '-',
            });
          }
        });
        setRows(nextRows);
      })
      .catch(() => message.error(text.loadFailed))
      .finally(() => setLoading(false));
  }, [monthKey]);

  const columns = [
    {
      title: text.teacher,
      dataIndex: 'teacher',
      key: 'teacher',
      render: (value, row) => (
        <Link to={`/salary-statistic/${year}/${month}/${row.teacherId}`}>
          {value}
        </Link>
      ),
    },
    {
      title: text.hourlyTotal,
      dataIndex: 'hourlyTotal',
      key: 'hourlyTotal',
      render: totalText,
    },
    {
      title: text.performanceCommission,
      dataIndex: 'performanceCommissionTotal',
      key: 'performanceCommissionTotal',
      render: totalText,
    },
    { title: text.total, dataIndex: 'total', key: 'total', render: totalText },
    { title: text.period, dataIndex: 'period', key: 'period' },
    { title: text.sentAt, dataIndex: 'sentAt', key: 'sentAt' },
  ];

  return (
    <Card
      title={`${year}\u5e74${Number(month)}\u6708${text.salary}`}
      style={{ marginTop: 10 }}
      extra={<Link to='/salary-statistic'>{text.back}</Link>}
    >
      <Spin spinning={loading}>
        {rows.length ? (
          <Table
            dataSource={rows}
            columns={columns}
            pagination={false}
            summary={(pageData) => {
              const hourlyTotal = pageData.reduce(
                (sum, row) => sum + Number(row.hourlyTotal || 0),
                0
              );
              const performanceTotal = pageData.reduce(
                (sum, row) => sum + Number(row.performanceCommissionTotal || 0),
                0
              );
              const total = pageData.reduce(
                (sum, row) => sum + Number(row.total || 0),
                0
              );
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>{text.total}</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>{totalText(hourlyTotal)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>{totalText(performanceTotal)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>{totalText(total)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              );
            }}
          />
        ) : (
          <Empty />
        )}
      </Spin>
    </Card>
  );
};

const SalaryStatisticDetail = ({ year, month, teacherId }) => {
  const [loading, setLoading] = useState(true);
  const [teacher, setTeacher] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;

  useEffect(() => {
    setLoading(true);
    profileService
      .getUser(teacherId)
      .then((res) => {
        const user = res.data || {};
        setTeacher(user);
        const localTarget = getStoredSalaryRecords().find(
          (item) =>
            item.teacherId === teacherId &&
            getSettlementMonthKey(item) === monthKey
        );
        const target = localTarget || (user.salaryStatisticSettlements || user.salarySettlements || []).find(
          (item) => getSettlementMonthKey(item) === monthKey
        );
        setSettlement(target || null);
      })
      .catch(() => message.error(text.loadFailed))
      .finally(() => setLoading(false));
  }, [monthKey, teacherId]);

  return (
    <Card
      title={`${teacher ? teacher.cnname || teacher.name : ''} ${year}\u5e74${Number(month)}\u6708${text.salary}`}
      style={{ marginTop: 10 }}
      extra={<Link to={`/salary-statistic/${year}/${month}`}>{text.backMonth}</Link>}
    >
      <Spin spinning={loading}>
        {settlement ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              {text.period}: {settlement.from || '-'} - {settlement.to || '-'}
              <span style={{ marginLeft: 24 }}>
                {text.sentAt}: {settlement.createdAt ? settlement.createdAt.slice(0, 10) : '-'}
              </span>
            </div>
            <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>{text.hourlyFee}</div>
            <Table
              size='small'
              dataSource={settlement.salaryRows || []}
              columns={settledHourlyColumns}
              pagination={false}
              summary={(pageData) => {
                const total = pageData.reduce(
                  (sum, row) => sum + Number(row.settlement || 0),
                  0
                );
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>{text.total}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell index={3}>{totalText(total)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
            <div style={{ fontWeight: 600, margin: '20px 0 8px' }}>{text.commission}</div>
            <Table
              size='small'
              dataSource={settlement.commissionRows || []}
              columns={settledCommissionColumns}
              pagination={false}
              tableLayout='fixed'
              summary={(pageData) => {
                const receiptTotal = pageData.reduce(
                  (sum, row) => sum + Number(row.total_aud || 0),
                  0
                );
                const performanceTotal = pageData.reduce(
                  (sum, row) => sum + Number(row.performanceCommissionAmount || 0),
                  0
                );
                const referralTotal = pageData.reduce(
                  (sum, row) => sum + Number(row.referralCommissionAmount || 0),
                  0
                );
                const incomeTotal = pageData.reduce(
                  (sum, row) => sum + Number(row.performanceIncome || 0),
                  0
                );
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>{text.total}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2}>{totalText(receiptTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{totalText(performanceTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{totalText(referralTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{totalText(incomeTotal)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
            <div style={{ fontWeight: 600, margin: '20px 0 8px' }}>{text.wage}</div>
            <Table
              size='small'
              dataSource={settlement.wageRows || []}
              columns={settledWageColumns}
              pagination={false}
            />
          </div>
        ) : (
          <Empty />
        )}
      </Spin>
    </Card>
  );
};

const SalaryStatisticList = ({ history }) => {
  const [years, setYears] = useState([]);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  useEffect(() => {
    const storedYears = getStoredYears();
    setYears(storedYears);
  }, []);

  const saveYears = (nextYears) => {
    const sortedYears = nextYears.sort((a, b) => Number(b) - Number(a));
    localStorage.setItem(storageKey, JSON.stringify(sortedYears));
    setYears(sortedYears);
  };

  const handleCreate = () => {
    const yearNumbers = years.map((year) => Number(year)).filter(Boolean);
    const latestYear = yearNumbers.length
      ? Math.max(...yearNumbers)
      : new Date().getFullYear() - 1;
    const year = String(latestYear + 1);
    const nextYears = [year, ...years];
    saveYears(nextYears);
    message.success(text.created);
  };

  const handleSelectMonth = (year, month) => {
    history.push(`/salary-statistic/${year}/${month}`);
  };

  const handleDeleteYear = (year) => {
    const nextYears = years.filter((item) => item !== year);
    saveYears(nextYears);
  };

  return (
    <Card
      title={text.title}
      style={{ marginTop: 10 }}
    >
      <div style={{ width: 160, textAlign: 'center', marginBottom: 12 }}>
        <Button type='primary' onClick={handleCreate}>
          {text.createYear}
        </Button>
      </div>
      {years.map((year) => (
        <div
          key={year}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ width: 80, fontWeight: 600 }}>{year}</div>
          <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap' }}>
            {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => {
              const isCurrentMonth = Number(year) === currentYear && month === currentMonth;
              return (
                <Button
                  key={`${year}-${month}`}
                  type='default'
                  style={{
                    marginRight: 8,
                    marginBottom: 8,
                    ...(isCurrentMonth
                      ? {
                          color: '#fff',
                          backgroundColor: '#13a683',
                          borderColor: '#13a683',
                        }
                      : {}),
                  }}
                  onClick={() => handleSelectMonth(year, month)}
                >
                  {month}
                  {text.month}
                </Button>
              );
            })}
          </div>
          <div style={{ width: 110, textAlign: 'right' }}>
            {Number(year) === currentYear && (
              <span style={{ color: '#13a683' }}>{'\u2190'} {text.currentYear}</span>
            )}
            {Number(year) > currentYear && (
              <Button size='small' danger onClick={() => handleDeleteYear(year)}>
                {text.delete}
              </Button>
            )}
          </div>
        </div>
      ))}
    </Card>
  );
};

const SalaryStatistic = ({ history, match }) => {
  const params = (match && match.params) || {};

  if (params.year && params.month && params.teacherId) {
    return (
      <SalaryStatisticDetail
        year={params.year}
        month={params.month}
        teacherId={params.teacherId}
      />
    );
  }

  if (params.year && params.month) {
    return <SalaryStatisticMonth year={params.year} month={params.month} />;
  }

  return <SalaryStatisticList history={history} />;
};

export default SalaryStatistic;
